import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useRegisterUser } from "@theprelude/core";
import {
  Button,
  Form,
  InputFormField,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { useForm } from "react-hook-form";
import { z } from "zod";
import AuthPage from "@/components/auth-page";

export const Route = createFileRoute("/sign-up")({
  beforeLoad: async ({ context }) => {
    const authConfig = await context.auth.getAuthConfig();

    if (authConfig.status === "authenticated") {
      return redirect({ to: "/home" });
    }
  },
  component: SignUpPage,
});

const signUpFormSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  name: z.string().optional(),
  company: z.string().min(1, "Company is required"),
});

interface SignUpForm {
  email: string;
  name: string;
  company: string;
}

function SignUpPage() {
  const navigate = useNavigate();
  const signUp = useRegisterUser();
  const form = useForm<SignUpForm>({
    defaultValues: {
      email: "",
      name: "",
      company: "",
    },
    resolver: zodResolver(signUpFormSchema),
  });

  const { register } = form;

  const submit = (data: SignUpForm) => {
    signUp.mutate(
      {
        email: data.email,
        name: data.name,
        company: data.company,
      },
      {
        onSuccess: () => {
          notifySuccess({
            message:
              "Account registered successfully. Please check your email for verification.",
          });

          void navigate({
            to: "/sign-in",
            search: { email: data.email },
          });
        },
        onError: (error) => {
          notifyError({ message: "Failed to register account.", error });
        },
      },
    );
  };

  return (
    <AuthPage title="Welcome to Prelude">
      <main>
        <>
          <Form
            {...form}
            onSubmitValid={submit}
            className="flex w-full flex-col gap-2"
          >
            <InputFormField
              label="Full name"
              {...register("name")}
              placeholder={"Optional"}
            />

            <InputFormField
              label="Company"
              {...register("company")}
              placeholder={"Required"}
              required
            />

            <InputFormField
              label="Email"
              {...register("email")}
              type={"email"}
              placeholder={"Required"}
              required
            />

            <div className="flex items-center gap-2 py-2">
              <p>
                By creating an account, you agree to our{" "}
                <a
                  href="https://preludesecurity.com/detect-terms"
                  target={"_blank"}
                  rel="noreferrer"
                  className="underline"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href="https://www.preludesecurity.com/privacy"
                  target={"_blank"}
                  rel="noreferrer"
                  className="underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
            <Button
              loading={signUp.isPending}
              disabled={signUp.isPending}
              intent="primary"
              type="submit"
              className="flex w-full justify-center"
            >
              Sign up
            </Button>
          </Form>
          <div className="flex flex-col gap-2 pt-8">
            <span className="text-lg">Already have an account?</span>
            <Button
              intent="secondary"
              className="flex w-full justify-center"
              onClick={() => navigate({ to: "/sign-in" })}
            >
              Sign in
            </Button>
          </div>
        </>
      </main>
    </AuthPage>
  );
}
